import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useDistricts, useProvinces } from '../../lib/geo'
import type { GeoRow } from '../../lib/geo'
import { useStorageUpload } from '../../lib/upload'
import { titleCase } from '../../lib/format'
import { PATCH_ASSIGNABLE_ROLE_KEYS, SYSTEM_ROLES, systemRoleTitle } from '../../lib/systemRoles'
import type { Organization, UserProfile } from '../../types'
import {
  Alert, Avatar, Button, Card, CardBody, Input, Select, Skeleton,
} from '../../components/ui'

/* Port of admin/templates/user_edit_wizard.html + admin/routes.py's
 * user_edit() (~384-508). One <form> spanning three visually-toggled steps
 * (Identity / Access & Scope / Review), submitted once as a single PATCH -
 * matching the Jinja original, which is also one <form> with three
 * data-wizard-panel divs and no per-step network round trip. */

interface AdminProfile extends UserProfile {
  displayName?: string
  dob?: string
  gender?: string
  id_document_type?: string
  id_document_number?: string
  id_document_url?: string
  id_document_name?: string
  id_document_front_storage_path?: string
  id_document_front_name?: string
  id_document_back_storage_path?: string
  id_document_back_name?: string
  roles?: string[]
  access_roles?: string[]
  reports_to_uid?: string
  all_orgs_access?: boolean
  org_access?: string[]
  truck_access?: string[]
  equipment_access?: string[]
  load_access?: string[]
}

// Lean shapes for the individual-resource-grant pickers below - only what's
// needed to label a checkbox, not the full records those admin list
// endpoints otherwise return.
interface TruckOption { id?: string; plate?: string; org_name?: string }
interface EquipmentOption { id?: string; name?: string; equipment_type?: string; org_id?: string }
interface LoadOption {
  id?: string; mineral_type?: string; tonnage?: number
  source_label?: string; destination_label?: string; load_time?: string; created_at?: string
}

// Only `profile` is read on this page - the bundle also carries kyc_docs /
// course_progress / organization (see gov/UserDetail.tsx for a consumer
// that uses those), which the Jinja edit wizard doesn't show either.
interface UserBundle { ok: boolean; profile: AdminProfile }

const STEP_LABELS = ['Identity', 'Access', 'Review'] as const

export default function AdminUserEdit() {
  const { uid } = useParams<{ uid: string }>()

  const bundleQuery = useQuery({
    queryKey: ['gov', 'user', uid],
    queryFn: () => api.get<UserBundle>(`/gov/users/${uid}`),
    enabled: Boolean(uid),
  })
  const orgsQuery = useQuery({
    queryKey: ['admin', 'orgs', 'all'],
    queryFn: () => api.get<{ ok: boolean; items: Organization[] }>('/admin/orgs?status=&limit=200'),
  })
  // Candidate "reports to" managers - mirrors admin/routes.py user_edit()'s
  // `[u for u in list_users(status="active", limit=1000) if u.get("uid") != uid]`.
  const managersQuery = useQuery({
    queryKey: ['admin', 'users', 'active'],
    queryFn: () => api.get<{ ok: boolean; items: UserProfile[] }>('/admin/users?status=active&limit=1000'),
  })
  // Platform-wide resource pickers for individual-resource grants (see
  // role_service.granted_truck_ids() et al.) - a super admin can hand any
  // user sight of specific trucks/equipment/loads regardless of org, so
  // these are unscoped lists to search across, not the target user's own org.
  const trucksQuery = useQuery({
    queryKey: ['admin', 'trucks', 'all'],
    queryFn: () => api.get<{ ok: boolean; items: TruckOption[] }>('/admin/trucks'),
  })
  const equipmentQuery = useQuery({
    queryKey: ['admin', 'hired-equipment', 'all'],
    queryFn: () => api.get<{ ok: boolean; items: EquipmentOption[] }>('/admin/hired-equipment'),
  })
  const loadsQuery = useQuery({
    queryKey: ['admin', 'loads', 'all'],
    queryFn: () => api.get<{ ok: boolean; items: LoadOption[] }>('/admin/loads'),
  })
  const provinces = useProvinces()

  if (!uid) return <Navigate to="/users" replace />

  if (bundleQuery.isError) {
    return (
      <Alert tone="danger">
        {(bundleQuery.error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/users">Back to users</Link></div>
      </Alert>
    )
  }
  if (bundleQuery.isLoading || !bundleQuery.data) {
    return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={260} /></div>
  }

  const profile = bundleQuery.data.profile
  const orgs = orgsQuery.data?.items ?? []
  const managers = (managersQuery.data?.items ?? []).filter((m) => m.uid !== uid)

  return (
    // key={uid} guarantees a fresh mount (and fresh internal state) if the
    // route param ever changes without an intervening full page load -
    // otherwise every useState below would keep the previous user's values.
    <EditWizard
      key={uid} uid={uid} profile={profile} orgs={orgs} managers={managers} provinces={provinces}
      trucks={trucksQuery.data?.items ?? []}
      equipment={equipmentQuery.data?.items ?? []}
      loads={loadsQuery.data?.items ?? []}
    />
  )
}

function EditWizard({
  uid, profile, orgs, managers, provinces, trucks, equipment, loads,
}: {
  uid: string; profile: AdminProfile; orgs: Organization[]; managers: UserProfile[]; provinces: GeoRow[]
  trucks: TruckOption[]; equipment: EquipmentOption[]; loads: LoadOption[]
}) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user: sessionUser, refresh } = useAuth()
  const isSelf = profile.uid === sessionUser?.uid

  const [step, setStep] = useState(0)

  // ── Identity ──
  const [fullName, setFullName] = useState(profile.full_name || profile.displayName || '')
  const [phone, setPhone] = useState(profile.phone || '')
  const [nationalId, setNationalId] = useState(profile.national_id || '')
  const [idDocType, setIdDocType] = useState(profile.id_document_type || '')
  const [idDocNumber, setIdDocNumber] = useState(profile.id_document_number || '')
  const [dob, setDob] = useState(profile.dob || '')
  const [gender, setGender] = useState(profile.gender || '')
  const frontFileRef = useRef<HTMLInputElement>(null)
  const backFileRef = useRef<HTMLInputElement>(null)
  const frontUpload = useStorageUpload(`user_identity/${uid}/front`)
  const backUpload = useStorageUpload(`user_identity/${uid}/back`)

  // ── Access & scope ──
  const [role, setRole] = useState(profile.role || '')
  const allowedKeys: readonly string[] = PATCH_ASSIGNABLE_ROLE_KEYS
  const grantedKeys = new Set([...(profile.roles ?? []), ...(profile.access_roles ?? [])])
  // What "Additional roles" starts out checked with: only the part of
  // roles+access_roles that our fixed 7-key picker can actually represent.
  const originalSecondary = PATCH_ASSIGNABLE_ROLE_KEYS
    .filter((k) => grantedKeys.has(k) && k !== profile.role)
  // Currently-granted roles our picker can't show/edit at all - surfaced
  // read-only below so nothing is silently hidden from the admin.
  const outOfSetGrants = [...grantedKeys].filter((k) => k !== profile.role && !allowedKeys.includes(k))
  const accessRolesHasOutOfSetValue = (profile.access_roles ?? []).some((k) => !allowedKeys.includes(k))

  const [secondary, setSecondary] = useState<string[]>(originalSecondary)
  const [reportsToUid, setReportsToUid] = useState(profile.reports_to_uid || '')
  const [orgId, setOrgId] = useState(profile.org_id || '')
  const [regionId, setRegionId] = useState(profile.region_id || '')
  const [districtId, setDistrictId] = useState(profile.district_id || '')
  const [allOrgsAccess, setAllOrgsAccess] = useState(Boolean(profile.all_orgs_access))
  const [orgAccess, setOrgAccess] = useState<string[]>(profile.org_access ?? [])
  const [truckAccess, setTruckAccess] = useState<string[]>(profile.truck_access ?? [])
  const [equipmentAccess, setEquipmentAccess] = useState<string[]>(profile.equipment_access ?? [])
  const [loadAccess, setLoadAccess] = useState<string[]>(profile.load_access ?? [])

  const districts = useDistricts(regionId)

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        full_name: fullName.trim(),
        phone: phone.trim(),
        national_id: nationalId.trim(),
        id_document_type: idDocType,
        id_document_number: idDocNumber.trim(),
        dob,
        gender,
        reports_to_uid: reportsToUid,
        org_id: orgId,
        region_id: regionId,
        district_id: districtId,
        all_orgs_access: allOrgsAccess,
        org_access: orgAccess,
        truck_access: truckAccess,
        equipment_access: equipmentAccess,
        load_access: loadAccess,
      }

      // role/roles/access_roles: PATCH /admin/users/<uid> validates all
      // three against a fixed 7-key allow-list (see lib/systemRoles.ts's
      // PATCH_ASSIGNABLE_ROLE_KEYS) rather than the full dynamic role
      // catalog the Jinja original reads from - so these are only ever
      // sent when actually changed, and only ever with values from that
      // set (which is all our pickers can select in the first place), so a
      // save here can never 400 against a profile whose current role or
      // secondary roles already sit outside it.
      if (role !== profile.role) {
        body.role = role
        // Mirrors admin/routes.py user_edit()'s
        // `updates["tier"] = role_service.role_tier(new_role)` - sent
        // alongside every role change, never on its own. Skipping this
        // would leave a stale tier in place after a role change (e.g.
        // miner -> gov_inspector without updating tier off "mining_ops"),
        // and update_user_profile()'s post-write custom-claims refresh
        // reads the *stored* tier back out, so a stale value there would
        // bake straight into the user's Firebase claims and break
        // tier-based routing (roles.ts:portalFor()) for them.
        body.tier = SYSTEM_ROLES.find((r) => r.key === role)?.tier
      }
      const changed = JSON.stringify([...secondary].sort()) !== JSON.stringify([...originalSecondary].sort())
      if (changed) {
        body.roles = secondary
        // Fixes a real bug in the Jinja original: user_edit_wizard.html
        // pre-checks "Additional roles" from roles UNION access_roles, but
        // its POST handler only ever resubmits "roles" - access_roles is
        // never cleared server-side, so an admin who unchecks a role that
        // came from access_roles watches it silently reappear (pre-checked
        // again) next time they open this page, because access_roles still
        // has it. This JSON PATCH endpoint accepts writing access_roles
        // directly (the old POST handler never could), so once every
        // currently-granted secondary role is provably within the 7-key
        // allow-list, we clear access_roles alongside roles and there's
        // nothing left to shadow it back in. Left untouched - a residual,
        // unfixable-from-here gap - when access_roles already holds a
        // value outside that set (surfaced to the admin as outOfSetGrants
        // above so it's at least visible, not silently dropped).
        if (!accessRolesHasOutOfSetValue) body.access_roles = []
      }

      const frontFile = frontFileRef.current?.files?.[0]
      if (frontFile) {
        const uploaded = await frontUpload.upload(frontFile)
        body.id_document_front_storage_path = uploaded.storage_path
        body.id_document_front_name = uploaded.name
        body.id_document_front_content_type = uploaded.content_type
        body.id_document_front_uploaded_at = new Date().toISOString()
      }
      const backFile = backFileRef.current?.files?.[0]
      if (backFile) {
        const uploaded = await backUpload.upload(backFile)
        body.id_document_back_storage_path = uploaded.storage_path
        body.id_document_back_name = uploaded.name
        body.id_document_back_content_type = uploaded.content_type
        body.id_document_back_uploaded_at = new Date().toISOString()
      }

      return api.patch<{ ok: boolean; item: AdminProfile }>(`/admin/users/${uid}`, body)
    },
    onSuccess: async () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'user', uid] })
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      // Mirrors admin/routes.py's `if uid == g.user.get('uid'): session['user'] = updated` -
      // refresh the live session so a self role-change takes effect (e.g. a
      // portal switch) without requiring a manual sign-out/in.
      if (isSelf) await refresh()
      navigate(`/users/${uid}`)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (step < 2) { setStep((s) => s + 1); return }
    save.mutate()
  }

  const orgName = orgs.find((o) => o.id === orgId)?.name
  const regionName = provinces.find((p) => p.id === regionId)?.name
  const districtName = districts.find((d) => d.id === districtId)?.name

  return (
    <div className="stack" style={{ gap: 'var(--sp-6)' }}>
      <Card>
        <CardBody>
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--sp-4)' }}>
            <div className="row">
              <Avatar name={fullName} email={profile.email} size={56} />
              <div>
                <h2 style={{ margin: 0, fontSize: 'var(--text-xl)' }}>{fullName || profile.email || 'Unknown user'}</h2>
                <p className="page-head__sub" style={{ margin: 0 }}>{profile.email || 'No email'}</p>
                <p className="field__hint" style={{ marginTop: 4 }}>Current role: {systemRoleTitle(profile.role)}</p>
              </div>
            </div>
            <Link to={`/users/${uid}`}><Button type="button" variant="secondary">Cancel</Button></Link>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--sp-2)', marginBottom: 'var(--sp-6)' }}>
            {STEP_LABELS.map((label, i) => (
              <Button
                key={label} type="button" size="sm"
                variant={step === i ? 'primary' : 'secondary'}
                onClick={() => setStep(i)}
              >
                {label}
              </Button>
            ))}
          </div>

          <form onSubmit={onSubmit} className="stack">
            {save.isError && (
              <Alert tone="danger">{save.error instanceof ApiError ? save.error.message : 'Could not save those changes.'}</Alert>
            )}

            {/* Steps stay mounted continuously (CSS-hidden, not conditionally
                rendered) rather than unmounted between steps - same
                technique the Jinja original uses (Alpine's x-show is also a
                CSS display toggle, never a DOM removal). This matters for
                more than fidelity: the front/back file <input>s below are
                native, uncontrolled elements read via a ref only at final
                submit time (step 2) - unmounting step 0 on "Next" would
                have silently dropped whatever file the admin had already
                selected there. display:none also exempts a hidden step's
                `required` fields from native constraint validation, so
                Next/Save still only ever validates the step currently
                visible. */}
            <div className="stack" style={{ display: step === 0 ? undefined : 'none' }}>
                <h3 style={{ margin: 0 }}>Identity</h3>
                <div className="grid grid--2">
                  <Input label="Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
                  <Input label="Phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  <Input label="National ID" value={nationalId} onChange={(e) => setNationalId(e.target.value)} />
                  <Select label="ID Document Type" value={idDocType} onChange={(e) => setIdDocType(e.target.value)}>
                    <option value="">Not set</option>
                    <option value="nrc">NRC</option>
                    <option value="passport">Passport</option>
                    <option value="driver_license">Driver License</option>
                    <option value="employee_id">Employee ID</option>
                  </Select>
                  <Input label="ID Document Number" value={idDocNumber} onChange={(e) => setIdDocNumber(e.target.value)} />
                  <div />
                  <div>
                    <Input label="ID Document — Front side" type="file" ref={frontFileRef} accept=".pdf,.jpg,.jpeg,.png" />
                    {profile.id_document_front_storage_path ? (
                      <p className="field__hint">
                        On file:{' '}
                        <a href={`/admin/users/${uid}/id/front`} target="_blank" rel="noopener noreferrer">
                          {profile.id_document_front_name || 'View front'}
                        </a>
                      </p>
                    ) : profile.id_document_url ? (
                      <p className="field__hint">
                        Legacy file:{' '}
                        <a href={profile.id_document_url} target="_blank" rel="noopener noreferrer">
                          {profile.id_document_name || 'View'}
                        </a>
                      </p>
                    ) : null}
                    {frontUpload.progress !== null && <p className="field__hint">Uploading… {frontUpload.progress}%</p>}
                    {frontUpload.error && <Alert tone="danger">{frontUpload.error}</Alert>}
                  </div>
                  <div>
                    <Input label="ID Document — Back side" type="file" ref={backFileRef} accept=".pdf,.jpg,.jpeg,.png" />
                    {profile.id_document_back_storage_path && (
                      <p className="field__hint">
                        On file:{' '}
                        <a href={`/admin/users/${uid}/id/back`} target="_blank" rel="noopener noreferrer">
                          {profile.id_document_back_name || 'View back'}
                        </a>
                      </p>
                    )}
                    {backUpload.progress !== null && <p className="field__hint">Uploading… {backUpload.progress}%</p>}
                    {backUpload.error && <Alert tone="danger">{backUpload.error}</Alert>}
                  </div>
                  <Input label="Date of Birth" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
                  <Select label="Gender" value={gender} onChange={(e) => setGender(e.target.value)}>
                    <option value="">Not set</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Select>
                </div>
            </div>

            <div className="stack" style={{ display: step === 1 ? undefined : 'none' }}>
                <h3 style={{ margin: 0 }}>Access And Scope</h3>
                <Alert tone="warning">
                  <strong>Role changes update platform access.</strong> The selected role controls menus, report
                  visibility, capture permissions, and Firebase custom claims.
                </Alert>

                <div className="grid grid--2">
                  <Select label="Role" required value={role} onChange={(e) => setRole(e.target.value)}>
                    {!allowedKeys.includes(profile.role ?? '') && (
                      <option value={profile.role}>{systemRoleTitle(profile.role)} (current)</option>
                    )}
                    {PATCH_ASSIGNABLE_ROLE_KEYS.map((k) => <option key={k} value={k}>{systemRoleTitle(k)}</option>)}
                  </Select>
                  <Select label="Reports To" value={reportsToUid} onChange={(e) => setReportsToUid(e.target.value)}>
                    <option value="">No reporting manager</option>
                    {managers.map((m) => (
                      <option key={m.uid} value={m.uid}>{m.full_name || m.email} - {titleCase(m.role)}</option>
                    ))}
                  </Select>
                  <Select label="Organisation" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                    <option value="">None</option>
                    {orgs.map((o) => <option key={o.id || o.name} value={o.id ?? ''}>{o.name}</option>)}
                  </Select>
                  <Select
                    label="Region" value={regionId}
                    onChange={(e) => { setRegionId(e.target.value); setDistrictId('') }}
                  >
                    <option value="">None</option>
                    {provinces.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Select>
                  <Select
                    label="District" value={districtId}
                    onChange={(e) => setDistrictId(e.target.value)} disabled={!regionId}
                  >
                    <option value="">None</option>
                    {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </div>

                <div>
                  <label className="field__label" style={{ display: 'block', marginBottom: 6 }}>Additional roles</label>
                  <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                    {PATCH_ASSIGNABLE_ROLE_KEYS.filter((k) => k !== role).map((k) => (
                      <label
                        key={k} style={{ padding: 'var(--sp-2) var(--sp-3)' }}
                        className={`radio-opt${secondary.includes(k) ? ' radio-opt--checked' : ''}`}
                      >
                        <input
                          type="checkbox" checked={secondary.includes(k)}
                          onChange={() => setSecondary((cur) => (
                            cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]
                          ))}
                        />
                        {systemRoleTitle(k)}
                      </label>
                    ))}
                  </div>
                  <p className="field__hint" style={{ marginTop: 6 }}>Select one or more roles. Permissions are combined.</p>
                  {outOfSetGrants.length > 0 && (
                    <p className="field__hint" style={{ marginTop: 6 }}>
                      Also currently granted, not editable from this screen: {outOfSetGrants.map((k) => systemRoleTitle(k)).join(', ')}.
                    </p>
                  )}
                </div>

                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--sp-5)', marginTop: 'var(--sp-2)' }}>
                  <label className="field__label" style={{ display: 'block', marginBottom: 6 }}>Cross-cooperative access</label>
                  <p className="field__hint" style={{ marginBottom: 12 }}>
                    Let this user work in and see data for cooperatives beyond their own - for example a universal
                    HR officer who manages every cooperative. Grants here are in addition to their own cooperative.
                  </p>
                  <label
                    className="radio-opt"
                    style={{ marginBottom: 12, background: 'var(--warning-bg)', borderColor: 'var(--warning)' }}
                  >
                    <input type="checkbox" checked={allOrgsAccess} onChange={(e) => setAllOrgsAccess(e.target.checked)} />
                    All cooperatives (platform-wide access)
                  </label>
                  <p className="field__hint" style={{ marginBottom: 8 }}>...or pick specific cooperatives this user can also access:</p>
                  <div
                    className="grid grid--2"
                    style={{
                      maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)', padding: 'var(--sp-3)',
                    }}
                  >
                    {orgs.map((o) => (
                      <label key={o.id || o.name} className="row" style={{ gap: 8, fontSize: 'var(--text-sm)' }}>
                        <input
                          type="checkbox" checked={orgAccess.includes(o.id ?? '')}
                          onChange={() => setOrgAccess((cur) => (
                            cur.includes(o.id ?? '') ? cur.filter((x) => x !== o.id) : [...cur, o.id ?? '']
                          ))}
                        />
                        {o.name}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Individual-resource grants. Deliberately separate from
                    "Cross-cooperative access" above: that one widens an
                    org-scoped role to more whole cooperatives, this one is a
                    stand-alone allow-list of specific resources that works
                    for ANY user - including one with no org and no
                    operational role at all (an external auditor, a
                    specific miner). Backed by GET /ops/my-trucks,
                    /ops/my-equipment and /ops/my-loads, which return only
                    what's ticked here and nothing else. */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--sp-5)', marginTop: 'var(--sp-2)' }}>
                  <label className="field__label" style={{ display: 'block', marginBottom: 6 }}>
                    Specific trucks, equipment &amp; loads
                  </label>
                  <p className="field__hint" style={{ marginBottom: 12 }}>
                    Give this user view-only sight of individual resources, wherever they sit on the platform —
                    without granting a role or a whole cooperative. They&rsquo;ll see exactly what you tick here
                    and nothing more.
                  </p>

                  <ResourcePicker
                    label="Trucks" emptyText="No trucks on the platform yet."
                    options={trucks.map((t) => ({
                      id: t.id ?? '',
                      label: t.plate || t.id || 'Truck',
                      meta: t.org_name || '',
                    }))}
                    selected={truckAccess} onToggle={setTruckAccess}
                  />
                  <ResourcePicker
                    label="Hired equipment" emptyText="No hired equipment on the platform yet."
                    options={equipment.map((e) => ({
                      id: e.id ?? '',
                      label: e.name || titleCase(e.equipment_type) || 'Equipment',
                      meta: titleCase(e.equipment_type),
                    }))}
                    selected={equipmentAccess} onToggle={setEquipmentAccess}
                  />
                  <ResourcePicker
                    label="Loads" emptyText="No loads on the platform yet."
                    options={loads.map((l) => ({
                      id: l.id ?? '',
                      label: `${titleCase(l.mineral_type) || 'Load'}${l.tonnage != null ? ` · ${l.tonnage} t` : ''}`,
                      meta: [l.source_label, l.destination_label].filter(Boolean).join(' → ')
                        || (l.load_time || l.created_at || '').slice(0, 10),
                    }))}
                    selected={loadAccess} onToggle={setLoadAccess}
                  />
                </div>
            </div>

            <div className="stack" style={{ display: step === 2 ? undefined : 'none' }}>
                <h3 style={{ margin: 0 }}>Review</h3>
                <div className="grid grid--2">
                  <ReviewTile label="Name" value={fullName || '-'} />
                  <ReviewTile label="Phone" value={phone || '-'} />
                  <ReviewTile label="National ID" value={nationalId || '-'} />
                  <ReviewTile label="Role" value={systemRoleTitle(role) || '-'} />
                  <ReviewTile label="Organisation" value={orgName || 'None'} />
                  <ReviewTile label="Region / District" value={`${regionName || 'None'} / ${districtName || 'None'}`} />
                  <ReviewTile
                    label="Cross-cooperative"
                    value={allOrgsAccess ? 'All cooperatives'
                      : orgAccess.length ? `${orgAccess.length} extra cooperative(s)`
                      : 'Own cooperative only'}
                  />
                  <ReviewTile
                    label="Specific resources"
                    value={
                      truckAccess.length + equipmentAccess.length + loadAccess.length === 0
                        ? 'None'
                        : [
                            truckAccess.length && `${truckAccess.length} truck(s)`,
                            equipmentAccess.length && `${equipmentAccess.length} equipment`,
                            loadAccess.length && `${loadAccess.length} load(s)`,
                          ].filter(Boolean).join(', ')
                    }
                  />
                </div>
                {isSelf && (
                  <Alert tone="danger">
                    You are editing your own account. If you change your own role, your current admin session will update after saving.
                  </Alert>
                )}
            </div>

            <div className="row" style={{ justifyContent: 'space-between', marginTop: 'var(--sp-4)' }}>
              <Link to={`/users/${uid}`}><Button type="button" variant="secondary">Cancel</Button></Link>
              <div className="row">
                {step > 0 && <Button type="button" variant="secondary" onClick={() => setStep((s) => s - 1)}>Back</Button>}
                <Button type="submit" loading={save.isPending}>{step < 2 ? 'Next' : 'Save Changes'}</Button>
              </div>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  )
}

/* One searchable checkbox list of individual resources to grant. Search
 * matters here in a way it doesn't for the cooperative list above: that one
 * is bounded by however many cooperatives exist (single digits today), while
 * trucks and especially loads grow without bound, so a plain scroll box
 * would stop being usable. The count of what's already ticked stays visible
 * even while a search filters the list, so an admin can never lose track of
 * a selection that's currently filtered out of view. */
function ResourcePicker({
  label, options, selected, onToggle, emptyText,
}: {
  label: string
  options: { id: string; label: string; meta?: string }[]
  selected: string[]
  onToggle: (next: string[]) => void
  emptyText: string
}) {
  const [q, setQ] = useState('')
  const query = q.trim().toLowerCase()
  const shown = query
    ? options.filter((o) => `${o.label} ${o.meta ?? ''}`.toLowerCase().includes(query))
    : options

  return (
    <div style={{ marginBottom: 'var(--sp-5)' }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{label}</span>
        <span className="field__hint">{selected.length} selected</span>
      </div>
      {options.length === 0 ? (
        <p className="field__hint">{emptyText}</p>
      ) : (
        <>
          <Input
            placeholder={`Search ${label.toLowerCase()}…`}
            value={q} onChange={(e) => setQ(e.target.value)}
          />
          <div
            style={{
              maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', padding: 'var(--sp-3)', marginTop: 'var(--sp-2)',
            }}
          >
            {shown.length === 0 ? (
              <p className="field__hint" style={{ margin: 0 }}>Nothing matches &ldquo;{q}&rdquo;.</p>
            ) : shown.map((o) => (
              <label key={o.id} className="row" style={{ gap: 8, fontSize: 'var(--text-sm)', padding: '3px 0' }}>
                <input
                  type="checkbox" checked={selected.includes(o.id)}
                  onChange={() => onToggle(
                    selected.includes(o.id)
                      ? selected.filter((x) => x !== o.id)
                      : [...selected, o.id],
                  )}
                />
                <span>
                  {o.label}
                  {o.meta ? <span style={{ color: 'var(--text-muted)' }}> — {o.meta}</span> : null}
                </span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function ReviewTile({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: 'var(--bg-sunken)', borderRadius: 'var(--radius)', padding: 'var(--sp-4)' }}>
      <p style={{ margin: 0, fontSize: 'var(--text-xs)', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
        {label}
      </p>
      <p style={{ margin: '4px 0 0', fontWeight: 600, wordBreak: 'break-word' }}>{value}</p>
    </div>
  )
}
